import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Cpu, ChevronDown, ChevronRight, Plus, Edit3, Trash2,
    Loader2, AlertTriangle, X, Save, Factory, ShieldCheck, Calendar, Hash,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * SYS-001 · Operations "My System" section — embedded on the
 * canonical Operations project workspace.
 *
 * Reads the same `GET /projects/:id/equipment` envelope used by the
 * customer web + mobile screens (so an Ops save is reflected in
 * every surface without a duplicate schema).
 *
 * Writes require ``manage_projects``; the section falls back to
 * read-only for other Operations users.
 */
const DEFAULT_TYPES = [
    "Solar Panels", "Inverter", "Battery Storage", "EV Charger",
    "Consumer Unit", "Monitoring / Gateway", "Other",
];

const OP_STATUSES = ["operational", "faulty", "under_service", "decommissioned"];

const NOT_RECORDED = "Not recorded";

function humanDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return iso; }
}

function formatCapacity(cap) {
    if (!cap) return null;
    if (cap.value == null && !cap.unit) return null;
    if (cap.value == null) return cap.unit;
    if (!cap.unit) return String(cap.value);
    return `${cap.value} ${cap.unit}`;
}

const EMPTY_FORM = {
    equipment_type: "Solar Panels",
    manufacturer: "",
    model: "",
    serial_number: "",
    quantity: "",
    rated_capacity: "",
    capacity_unit: "",
    installation_date: "",
    commissioning_date: "",
    warranty: "",
    warranty_expiry: "",
    operational_status: "operational",
    customer_notes: "",
    internal_notes: "",
    image_url: "",
};

function equipmentToForm(eq) {
    return {
        equipment_type: eq.equipment_type || eq.type || "Other",
        manufacturer: eq.manufacturer || "",
        model: eq.model || "",
        serial_number: eq.serial_number || eq.serial_numbers || "",
        quantity: eq.quantity || "",
        rated_capacity: eq.rated_capacity?.value ?? "",
        capacity_unit: eq.rated_capacity?.unit || "",
        installation_date: eq.installation_date || "",
        commissioning_date: eq.commissioning_date || "",
        warranty: eq.warranty || "",
        warranty_expiry: eq.warranty_expiry || "",
        operational_status: eq.operational_status || "operational",
        customer_notes: eq.customer_notes || "",
        internal_notes: eq.internal_notes || "",
        image_url: eq.image_url || "",
    };
}

export default function MySystemSection({ project, currentUser }) {
    const projectId = project?.id;
    const canManage = useMemo(() => {
        // Operations users pass require_permission("manage_projects") on
        // the backend. Frontend mirror: `permissions` may not be attached
        // to every user record from /auth/me — fall back to role check
        // (operations superset) and let the backend refuse if not
        // authorised.
        const perms = currentUser?.permissions || [];
        if (perms.includes("manage_projects")) return true;
        return currentUser?.role === "operations" || currentUser?.role === "installer";
    }, [currentUser]);

    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [summary, setSummary] = useState(null);
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);      // null | { equipment_id? , form }
    const [confirmArchive, setConfirmArchive] = useState(null); // equipment doc
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        setErr(null);
        try {
            const resp = await api.listEquipment(projectId);
            if (Array.isArray(resp)) {
                setItems(resp);
                setSummary(null);
            } else {
                setItems(resp?.equipment || []);
                setSummary(resp?.system || null);
            }
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || "Failed to load system.");
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);

    const openAdd = () => setEditing({ form: { ...EMPTY_FORM } });
    const openEdit = (eq) => setEditing({ equipment_id: eq.id, form: equipmentToForm(eq) });
    const closeEditor = () => { setEditing(null); setSaving(false); };

    const submit = async () => {
        if (!editing) return;
        if (!editing.form.equipment_type?.trim()) {
            toast.error("Equipment type is required.");
            return;
        }
        setSaving(true);
        try {
            const body = {};
            Object.entries(editing.form).forEach(([k, v]) => {
                if (v === "" || v == null) return;
                if (k === "rated_capacity") body[k] = Number(v);
                else body[k] = v;
            });
            if (editing.equipment_id) {
                await api.opsUpdateEquipment(editing.equipment_id, body);
                toast.success("Equipment updated.");
            } else {
                await api.opsCreateEquipment(projectId, body);
                toast.success("Equipment added.");
            }
            closeEditor();
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const doArchive = async () => {
        if (!confirmArchive) return;
        try {
            await api.opsArchiveEquipment(confirmArchive.id);
            toast.success("Equipment archived.");
            setConfirmArchive(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Archive failed.");
        }
    };

    const activeItems = items.filter((it) => (it.status || "active") !== "archived");
    const archivedItems = items.filter((it) => (it.status || "active") === "archived");

    return (
        <section className="mt-6" data-testid="ops-mysystem-section">
            <div className="flex items-center justify-between mb-3">
                <button
                    data-testid="ops-mysystem-toggle"
                    onClick={() => setExpanded(!expanded)}
                    className="flex items-center gap-2 text-[#131B2F] hover:text-[#3E9E2E] transition-colors"
                >
                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <Cpu size={16} className="text-[#3E9E2E]" />
                    <span className="text-lg font-medium">
                        My System{!expanded && ` (${activeItems.length})`}
                    </span>
                </button>
                {canManage && (
                    <button
                        onClick={openAdd}
                        disabled={!projectId}
                        className="pe-btn-primary text-sm h-9"
                        data-testid="ops-mysystem-add"
                    >
                        <Plus size={14} /> Add equipment
                    </button>
                )}
            </div>

            {expanded && (
                <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 md:p-5">
                    {loading && (
                        <div className="p-6 flex items-center justify-center text-sm text-[#4B5565]" data-testid="ops-mysystem-loading">
                            <Loader2 className="animate-spin mr-2" size={16} /> Loading system…
                        </div>
                    )}

                    {!loading && err && (
                        <div className="p-6 text-center" data-testid="ops-mysystem-error">
                            <AlertTriangle size={20} className="mx-auto text-[#B91C1C]" />
                            <div className="mt-2 text-sm text-[#131B2F]">{err}</div>
                            <button onClick={load} className="mt-3 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]" data-testid="ops-mysystem-retry">
                                Try again
                            </button>
                        </div>
                    )}

                    {!loading && !err && (
                        <>
                            {summary && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                    <SummaryStat label="Active items" value={String(activeItems.length)} />
                                    <SummaryStat label="Archived" value={String(archivedItems.length)} />
                                    <SummaryStat label="Earliest install" value={humanDate(summary.earliest_installation_date) || NOT_RECORDED} />
                                    <SummaryStat label="Latest commission" value={humanDate(summary.latest_commissioning_date) || NOT_RECORDED} />
                                </div>
                            )}

                            {activeItems.length === 0 && archivedItems.length === 0 && (
                                <div className="p-6 text-center text-sm text-[#4B5565]" data-testid="ops-mysystem-empty">
                                    No equipment recorded yet. Use “Add equipment” to record the system as-installed.
                                </div>
                            )}

                            {activeItems.map((eq) => (
                                <EquipmentRow
                                    key={eq.id}
                                    eq={eq}
                                    canManage={canManage}
                                    onEdit={() => openEdit(eq)}
                                    onArchive={() => setConfirmArchive(eq)}
                                />
                            ))}

                            {archivedItems.length > 0 && (
                                <div className="mt-4 pt-3 border-t border-dashed border-[#E5E7EB]">
                                    <div className="text-[10px] uppercase tracking-wide text-[#4B5565] mb-2">Archived</div>
                                    {archivedItems.map((eq) => (
                                        <EquipmentRow
                                            key={eq.id}
                                            eq={eq}
                                            canManage={false}
                                            archived
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Add / edit dialog */}
            <Dialog open={!!editing} onOpenChange={(v) => !v && closeEditor()}>
                <DialogContent className="sm:max-w-lg" data-testid="ops-mysystem-editor">
                    <DialogHeader>
                        <DialogTitle>{editing?.equipment_id ? "Edit equipment" : "Add equipment"}</DialogTitle>
                    </DialogHeader>
                    {editing && (
                        <EditorForm
                            form={editing.form}
                            onChange={(patch) => setEditing((prev) => ({ ...prev, form: { ...prev.form, ...patch } }))}
                        />
                    )}
                    <DialogFooter>
                        <button
                            type="button"
                            onClick={closeEditor}
                            className="h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                            data-testid="ops-mysystem-editor-cancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={saving}
                            className="pe-btn-primary text-sm h-9"
                            data-testid="ops-mysystem-editor-save"
                        >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                            {editing?.equipment_id ? "Save changes" : "Add equipment"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Archive confirmation */}
            <Dialog open={!!confirmArchive} onOpenChange={(v) => !v && setConfirmArchive(null)}>
                <DialogContent className="sm:max-w-sm" data-testid="ops-mysystem-archive-confirm">
                    <DialogHeader>
                        <DialogTitle>Archive this equipment?</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-[#131B2F]">
                        The record stays in Operations for audit but no longer appears to the customer.
                    </div>
                    <DialogFooter>
                        <button
                            type="button"
                            onClick={() => setConfirmArchive(null)}
                            className="h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                            data-testid="ops-mysystem-archive-cancel"
                        >
                            Keep it
                        </button>
                        <button
                            type="button"
                            onClick={doArchive}
                            className="h-9 px-3 rounded-md bg-[#B91C1C] text-white text-sm hover:bg-[#991B1B] inline-flex items-center gap-1.5"
                            data-testid="ops-mysystem-archive-confirm-btn"
                        >
                            <Trash2 size={14} /> Archive
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}

function SummaryStat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wide text-[#4B5565]">{label}</div>
            <div className="text-[#131B2F] font-medium mt-0.5 text-sm">{value}</div>
        </div>
    );
}

function EquipmentRow({ eq, canManage, onEdit, onArchive, archived }) {
    const type = eq.equipment_type || eq.type || "Equipment";
    const display = eq.display_name || eq.model || NOT_RECORDED;
    const capacity = formatCapacity(eq.rated_capacity);
    return (
        <div
            className={"py-3 border-b last:border-b-0 border-[#EDEFF3] " + (archived ? "opacity-60" : "")}
            data-testid={`ops-mysystem-row-${eq.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="pe-chip !bg-[#F5F5F7] text-[10px]">{type}</span>
                        <span className="text-sm font-medium text-[#131B2F] truncate">{display}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#4B5565]">
                        {eq.manufacturer && <span className="inline-flex items-center gap-1"><Factory size={11} /> {eq.manufacturer}</span>}
                        {eq.quantity && <span>Qty: {eq.quantity}</span>}
                        {capacity && <span>{capacity}</span>}
                        {eq.serial_number && <span className="inline-flex items-center gap-1"><Hash size={11} /> {eq.serial_number}</span>}
                        {humanDate(eq.installation_date) && <span className="inline-flex items-center gap-1"><Calendar size={11} /> Installed {humanDate(eq.installation_date)}</span>}
                        {humanDate(eq.warranty_expiry) && <span className="inline-flex items-center gap-1"><ShieldCheck size={11} /> Warranty until {humanDate(eq.warranty_expiry)}</span>}
                        {eq.operational_status && <span className="text-[#3E9E2E]">· {eq.operational_status}</span>}
                    </div>
                    {eq.internal_notes && (
                        <div className="mt-1 text-[11px] text-[#78350F] bg-[#FFFBEB] border border-[#FDE68A] rounded px-2 py-1">
                            Internal · {eq.internal_notes}
                        </div>
                    )}
                </div>
                {canManage && !archived && (
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            onClick={onEdit}
                            className="h-8 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#131B2F] hover:bg-[#F5F5F7] inline-flex items-center gap-1"
                            data-testid={`ops-mysystem-edit-${eq.id}`}
                        >
                            <Edit3 size={12} /> Edit
                        </button>
                        <button
                            onClick={onArchive}
                            className="h-8 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-xs text-[#B91C1C] hover:bg-[#FEF2F2] inline-flex items-center gap-1"
                            data-testid={`ops-mysystem-archive-${eq.id}`}
                        >
                            <Trash2 size={12} /> Archive
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function EditorForm({ form, onChange }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Field label="Equipment type *">
                <select
                    className="pe-input"
                    value={form.equipment_type}
                    onChange={(e) => onChange({ equipment_type: e.target.value })}
                    data-testid="ops-mysystem-field-type"
                >
                    {DEFAULT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            </Field>
            <Field label="Manufacturer">
                <input className="pe-input" value={form.manufacturer} onChange={(e) => onChange({ manufacturer: e.target.value })} data-testid="ops-mysystem-field-manufacturer" />
            </Field>
            <Field label="Model">
                <input className="pe-input" value={form.model} onChange={(e) => onChange({ model: e.target.value })} data-testid="ops-mysystem-field-model" />
            </Field>
            <Field label="Serial number">
                <input className="pe-input" value={form.serial_number} onChange={(e) => onChange({ serial_number: e.target.value })} data-testid="ops-mysystem-field-serial" />
            </Field>
            <Field label="Quantity">
                <input className="pe-input" value={form.quantity} onChange={(e) => onChange({ quantity: e.target.value })} placeholder="e.g. 10 panels" data-testid="ops-mysystem-field-quantity" />
            </Field>
            <Field label="Rated capacity">
                <div className="flex gap-2">
                    <input className="pe-input flex-1" value={form.rated_capacity} onChange={(e) => onChange({ rated_capacity: e.target.value })} placeholder="e.g. 5.2" inputMode="decimal" data-testid="ops-mysystem-field-capacity" />
                    <input className="pe-input w-20" value={form.capacity_unit} onChange={(e) => onChange({ capacity_unit: e.target.value })} placeholder="kWh" data-testid="ops-mysystem-field-capacity-unit" />
                </div>
            </Field>
            <Field label="Installation date">
                <input type="date" className="pe-input" value={form.installation_date} onChange={(e) => onChange({ installation_date: e.target.value })} data-testid="ops-mysystem-field-install" />
            </Field>
            <Field label="Commissioning date">
                <input type="date" className="pe-input" value={form.commissioning_date} onChange={(e) => onChange({ commissioning_date: e.target.value })} data-testid="ops-mysystem-field-commission" />
            </Field>
            <Field label="Warranty (free text)">
                <input className="pe-input" value={form.warranty} onChange={(e) => onChange({ warranty: e.target.value })} data-testid="ops-mysystem-field-warranty" />
            </Field>
            <Field label="Warranty expiry">
                <input type="date" className="pe-input" value={form.warranty_expiry} onChange={(e) => onChange({ warranty_expiry: e.target.value })} data-testid="ops-mysystem-field-warranty-expiry" />
            </Field>
            <Field label="Operational status">
                <select className="pe-input" value={form.operational_status} onChange={(e) => onChange({ operational_status: e.target.value })} data-testid="ops-mysystem-field-status">
                    {OP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </Field>
            <Field label="Image URL">
                <input className="pe-input" value={form.image_url} onChange={(e) => onChange({ image_url: e.target.value })} data-testid="ops-mysystem-field-image" />
            </Field>
            <Field label="Customer-visible notes" full>
                <textarea className="pe-input min-h-[60px]" value={form.customer_notes} onChange={(e) => onChange({ customer_notes: e.target.value })} data-testid="ops-mysystem-field-notes" />
            </Field>
            <Field label="Internal notes (never shown to customer)" full>
                <textarea className="pe-input min-h-[60px] bg-[#FFFBEB]" value={form.internal_notes} onChange={(e) => onChange({ internal_notes: e.target.value })} data-testid="ops-mysystem-field-internal" />
            </Field>
        </div>
    );
}

function Field({ label, children, full }) {
    return (
        <label className={"flex flex-col gap-1 " + (full ? "md:col-span-2" : "")}>
            <span className="text-[11px] uppercase tracking-wide text-[#4B5565]">{label}</span>
            {children}
        </label>
    );
}
